import { useInventoryStore, MAX_LEVEL, MAX_STARS, type OwnedPokemon } from '~/stores/useInventoryStore'
import { useDaycareStore } from '~/stores/useDaycareStore'
import {
  cleanupResolvedDaycareState,
  getBestDaycareCandidates,
  pickInventoryCandidateForQueueEntry,
} from '~/utils/daycare'

/**
 * Retire slots / file / derniers éclos pour les espèces déjà shiny 5★,
 * puis remplit les slots vides : file (FIFO) → derniers éclos → candidats utiles (objectif shiny).
 * Retourne le nombre de Pokémon effectivement déposés.
 */
export function applyDaycareInventoryRefill(maxSlots: number): number {
  const inventory = useInventoryStore()
  const daycare = useDaycareStore()
  let deposited = 0

  const cleaned = cleanupResolvedDaycareState(
    inventory.collection,
    daycare.slots,
    daycare.reserveQueue,
    daycare.lastDeposited,
    MAX_STARS
  )
  // `cleaned.slots`/`cleaned.lastDeposited` peuvent être les mêmes références que celles
  // déjà dans le store (cas « rien à nettoyer »). On passe systématiquement par les actions
  // de mutation en place pour éviter la récursion `nm.set → set value → nm.set` provoquée
  // par l'assignation directe d'un tableau sur un state Pinia (ObjectRefImpl).
  daycare.setSlots(cleaned.slots)
  daycare.setReserveQueue(cleaned.reserveQueue)
  daycare.setLastDeposited(cleaned.lastDeposited)
  daycare.addReserveFillPending(cleaned.removedSlots)
  const reserveSlotsToFill = daycare.reserveFillPending

  const excludedIds = new Set<number>()
  let reserveDeposited = 0

  const tryDeposit = (candidate: OwnedPokemon): boolean => {
    if (daycare.slots.length >= maxSlots) return false
    if (!candidate) return false
    // Ne pas retirer l’équipe active lors d’un remplissage automatique (file / éclos / suggestions).
    if (candidate.teamSlot !== null && candidate.teamSlot !== undefined) {
      return false
    }
    const ok = daycare.deposit(
      {
        slug: candidate.slug,
        nameFr: candidate.nameFr,
        nameEn: candidate.nameEn,
        stars: candidate.stars,
        rarity: candidate.rarity,
        isShiny: candidate.isShiny,
        cosmeticFormSlug: candidate.cosmeticFormSlug ?? null,
      },
      maxSlots
    )
    if (ok) {
      excludedIds.add(candidate.id)
      deposited++
    }
    return ok
  }

  const remainingReserve = []
  for (const entry of daycare.reserveQueue) {
    if (daycare.slots.length >= maxSlots) {
      remainingReserve.push(entry)
      continue
    }
    if (reserveSlotsToFill > 0 && reserveDeposited >= reserveSlotsToFill) {
      remainingReserve.push(entry)
      continue
    }
    const candidate = pickInventoryCandidateForQueueEntry(
      inventory.collection,
      daycare.slots,
      entry,
      MAX_LEVEL,
      MAX_STARS,
      excludedIds,
      true
    )
    if (!candidate || !tryDeposit(candidate)) {
      remainingReserve.push(entry)
    } else {
      reserveDeposited++
    }
  }
  daycare.setReserveQueue(remainingReserve)
  daycare.consumeReserveFillPending(reserveDeposited)

  while (daycare.reserveFillPending > 0 && daycare.slots.length < maxSlots) {
    const before = daycare.slots.length
    for (const entry of daycare.lastDeposited) {
      if (daycare.slots.length >= maxSlots) break
      const candidate = pickInventoryCandidateForQueueEntry(
        inventory.collection,
        daycare.slots,
        entry,
        MAX_LEVEL,
        MAX_STARS,
        excludedIds,
        true
      )
      if (candidate) tryDeposit(candidate)
    }
    const delta = daycare.slots.length - before
    if (delta > 0) {
      daycare.consumeReserveFillPending(Math.min(delta, daycare.reserveFillPending))
      continue
    }
    break
  }

  if (daycare.reserveFillPending === 0) {
    for (const entry of daycare.lastDeposited) {
      if (daycare.slots.length >= maxSlots) break
      const candidate = pickInventoryCandidateForQueueEntry(
        inventory.collection,
        daycare.slots,
        entry,
        MAX_LEVEL,
        MAX_STARS,
        excludedIds,
        true
      )
      if (candidate) tryDeposit(candidate)
    }
  }

  while (daycare.slots.length < maxSlots) {
    const bestCandidates = getBestDaycareCandidates(
      inventory.collection,
      daycare.slots,
      MAX_LEVEL,
      MAX_STARS,
      {
        shinyGoal: true,
        hideOwnedShiny: false,
        excludeTeamMembers: true,
        daycareAutoPickOrder: true,
      }
    )
    let depositedAny = false
    for (const candidate of bestCandidates) {
      if (daycare.slots.length >= maxSlots) break
      if (excludedIds.has(candidate.id)) continue
      if (tryDeposit(candidate)) depositedAny = true
    }
    if (!depositedAny) break
  }

  return deposited
}
